"use client";

import React from "react";
import { Wrench, Search, Copy, Sliders, PackageX, FileSpreadsheet, FileCode2, Gauge, Code2, CornerDownRight, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function TechnicalEcommerce() {
  const technicalTopics = [{
    title: "Crawlability and indexability",
    description: "robots.txt, internal links and crawl-trap identification (filters, sort parameters, internal search pages) decide whether crawl budget goes to important product and category pages or junk URLs.",
    icon: Search
  }, {
    title: "Canonical URLs and duplicates",
    description: "Canonical tags consolidate ranking signals for variants and tracking parameters; without them one product can exist at several URLs, splitting authority.",
    icon: Copy
  }, {
    title: "Product variants and faceted navigation",
    description: "Size and colour variants can create hundreds of near-identical pages, and filter combinations multiply it. I decide which combinations to index, which to canonicalise and which to block.",
    icon: Sliders
  }, {
    title: "Out-of-stock and discontinued products",
    description: "Deleting pages without redirects loses ranking signals. Depending on data and demand: keep indexable with alternatives, redirect to the nearest relevant category, or reuse the URL for a replacement product.",
    icon: PackageX
  }, {
    title: "Pagination and crawl paths",
    description: "Products buried behind pagination or JavaScript-loaded grids may never be crawled; ensure crawl paths reach every indexable product.",
    icon: FileSpreadsheet
  }, {
    title: "XML sitemaps",
    description: "Separate sitemaps for products, categories and CMS pages with only canonical, indexable URLs.",
    icon: FileCode2
  }, {
    title: "Core Web Vitals and mobile usability",
    description: "LCP, INP/FID and CLS affect the mobile shopping experience and landing page usability.",
    icon: Gauge
  }, {
    title: "JavaScript rendering",
    description: "JS-heavy themes or headless builds can stop search engines seeing product content; identify rendering gaps and recommend solutions.",
    icon: Code2
  }, {
    title: "Redirect management",
    description: "Migrations, URL changes and discontinued products need clean redirect maps; redirect chains add load time and dilute signals.",
    icon: CornerDownRight
  }, {
    title: "Structured data validation",
    description: "Product, offer and review schema must pass validation and reflect live data; mismatches can lose rich results.",
    icon: Wrench
  }];
  return <section id="technical-ecommerce" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Store Infrastructure & Architecture
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Technical SEO for ecommerce websites
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Technical SEO for online stores is driven by catalogue size, platform setup and commercial priorities. I prioritise fixes by their revenue impact rather than delivering a generic automated checklist.
          </p>
        </div>

        {/* Technical Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {technicalTopics.map((topic, i) => {
          const Icon = topic.icon;
          return <Card key={topic.title} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs flex flex-col justify-between">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="size-4" />
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground">
                    {topic.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {topic.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Highlighted Strategic Panel: Product variants, faceted navigation and canonicalisation */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-xs mb-14">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary font-mono">
              Strategic Decision Framework
            </span>
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground mt-1">
              Product variants, faceted navigation and canonicalisation
            </h3>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Ecommerce sites regularly generate thousands of duplicate or near-identical URLs when visitors filter products by colour, size, price or brand. Without clear architecture, crawl resources get wasted on low-value permutations while core product and category URLs struggle for authority.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="p-5 rounded-xl border border-border bg-background/80 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="size-4 text-primary" />
                  <h4 className="text-base font-semibold text-foreground">1. When to index</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Filter combinations and variant pages with demonstrable search demand (such as &ldquo;blue velvet sofas&rdquo; or high-intent size/colour queries) are given unique metadata, targeted copy, and dedicated inclusion in the catalogue hierarchy.
                </p>
              </div>
            </div>

            <div className="p-5 rounded-xl border border-border bg-background/80 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="size-4 text-primary" />
                  <h4 className="text-base font-semibold text-foreground">2. When to canonicalise</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Variants and parameter URLs that offer minor variations without distinct search volume point canonical tags back to the parent category or main product URL, consolidating ranking signals and preventing split equity.
                </p>
              </div>
            </div>

            <div className="p-5 rounded-xl border border-border bg-background/80 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="size-4 text-primary" />
                  <h4 className="text-base font-semibold text-foreground">3. When to block or noindex</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Multi-select filter traps, internal sorting parameters, and session-based URLs are blocked via robots.txt or tagged with noindex instructions to protect crawl budget from endless duplicate parameter loops.
                </p>
              </div>
            </div>
          </div>

          {/* Out-of-stock and discontinued handling */}
          <div className="mt-8 pt-6 border-t border-border/70">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-2">
              Handling out-of-stock and discontinued inventory
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Deleting sold-out products without a plan destroys accumulated search equity. Depending on historical traffic and search demand, I help stores keep pages indexable while offering relevant alternatives, configure 301 redirects to the nearest parent collection, or reuse URLs when replacement models arrive.
            </p>
          </div>
        </div>

        {/* Related Links in Context */}
        <div className="pt-4 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            Explore deeper technical solutions for complex catalogue architecture:
          </p>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-5 text-sm">
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              Technical SEO services
              <ArrowRight className="size-3.5" />
            </Link>
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              Ecommerce SEO audits
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}